import types from '../mutation-types';
import BulkEmailActionsAPI from '../../api/bulkEmailActions';
import { getEmailProvider } from '../../helper/bulkEmailActionContext';

export const state = {
  uiFlags: {
    isProcessing: false,
    isMoving: false,
    isTrashing: false,
    isArchiving: false,
  },
};

export const getters = {
  getUIFlags(_state) {
    return _state.uiFlags;
  },
};

export const actions = {
  /**
   * Move conversations to trash
   * @param {Object} context - Vuex context
   * @param {Object} payload - { conversationIds, inboxId }
   */
  batchTrash: async function batchTrashAction(
    { commit, rootGetters },
    { conversationIds, inboxId }
  ) {
    commit(types.SET_BULK_EMAIL_UI_FLAG, {
      isProcessing: true,
      isTrashing: true,
    });
    try {
      const inbox = rootGetters['inboxes/getInbox'](inboxId);
      if (!inbox) {
        throw new Error('Inbox not found');
      }

      const provider = getEmailProvider(inbox);
      if (!provider) {
        throw new Error('Not an email inbox');
      }

      await BulkEmailActionsAPI.batchTrash({
        conversationIds,
      });

      return { success: true };
    } catch (error) {
      throw new Error(error.message || 'Failed to move to trash');
    } finally {
      commit(types.SET_BULK_EMAIL_UI_FLAG, {
        isProcessing: false,
        isTrashing: false,
      });
    }
  },

  /**
   * Archive conversations (Gmail and Microsoft only)
   * @param {Object} context - Vuex context
   * @param {Object} payload - { conversationIds, inboxId }
   */
  batchArchive: async function batchArchiveAction(
    { commit, rootGetters },
    { conversationIds, inboxId }
  ) {
    commit(types.SET_BULK_EMAIL_UI_FLAG, {
      isProcessing: true,
      isArchiving: true,
    });
    try {
      const inbox = rootGetters['inboxes/getInbox'](inboxId);
      if (!inbox) {
        throw new Error('Inbox not found');
      }

      const provider = getEmailProvider(inbox);
      if (!provider) {
        throw new Error('Not an email inbox');
      }

      // IMAP does not support archive
      if (provider === 'imap') {
        throw new Error('Archive is not supported for IMAP inboxes');
      }

      await BulkEmailActionsAPI.batchArchive({
        conversationIds,
      });

      return { success: true };
    } catch (error) {
      throw new Error(error.message || 'Failed to archive');
    } finally {
      commit(types.SET_BULK_EMAIL_UI_FLAG, {
        isProcessing: false,
        isArchiving: false,
      });
    }
  },

  /**
   * Move conversations to a folder
   * @param {Object} context - Vuex context
   * @param {Object} payload - { conversationIds, inboxId, targetFolderId, targetFolderTitle, currentFolderId }
   */
  batchMove: async function batchMoveAction(
    { commit, rootGetters },
    {
      conversationIds,
      inboxId,
      targetFolderId,
      targetFolderTitle,
      currentFolderId = null,
    }
  ) {
    commit(types.SET_BULK_EMAIL_UI_FLAG, {
      isProcessing: true,
      isMoving: true,
    });
    try {
      const inbox = rootGetters['inboxes/getInbox'](inboxId);
      if (!inbox) {
        throw new Error('Inbox not found');
      }

      const provider = getEmailProvider(inbox);
      if (!provider) {
        throw new Error('Not an email inbox');
      }

      await BulkEmailActionsAPI.batchMove({
        conversationIds,
        targetFolderId,
        targetFolderTitle,
        currentFolderId,
      });

      return { success: true };
    } catch (error) {
      throw new Error(error.message || 'Failed to move conversations');
    } finally {
      commit(types.SET_BULK_EMAIL_UI_FLAG, {
        isProcessing: false,
        isMoving: false,
      });
    }
  },
};

export const mutations = {
  [types.SET_BULK_EMAIL_UI_FLAG](_state, data) {
    _state.uiFlags = {
      ..._state.uiFlags,
      ...data,
    };
  },
};

export default {
  namespaced: true,
  actions,
  state,
  getters,
  mutations,
};
